import { get } from 'lodash';
import { parse as parseRSS } from 'rss-to-json';
import {
  all,
  delay,
  put,
  call,
  take,
  select,
  takeEvery,
} from 'redux-saga/effects';
import * as VoxImplant from 'voximplant-websdk';

import { safeApply, safeCall } from './errors';

import { LISTEN_CALL_FAILURE, SELECT_CALL_BY_PBX_ID } from '../actions/calls';
import { SHOW_NOTIFICATION, STOP_TITLE_FLASHING } from '../actions/common';
import { TOGGLE_BUGS_DROPDOWN } from '../actions/general';
import {
  FETCH_DEPARTMENTS,
  FETCH_OPERATORS,
  FETCH_RECENT_CALLS,
  SET_CLIENT_INFO,
  SET_DEANINOMIZE,
  SET_DEPARTMENTS,
  SET_OPERATORS,
  SET_RECENT_CALLS,
  SET_TRANSFER_FAIL,
  TOGGLE_DEPARTMENTS,
  TOGGLE_OPERATORS,
  TOGGLE_RECENT_CALLS,
  TOGGLE_SHOWING,
  VOX_ANSWER,
  VOX_CALL_CONNECTED,
  VOX_CALL_DISCONNECTED,
  VOX_CALL_INCOMING,
  VOX_CALL_OUTGOING,
  VOX_CALL,
  VOX_CANCEL_HELP_REQUEST,
  VOX_CONNECT_SUPERVISOR,
  VOX_DEANINOMIZE,
  VOX_HANGUP,
  VOX_HOLD,
  VOX_LISTEN_START,
  VOX_LISTEN_STOP,
  VOX_LISTEN,
  VOX_LOG_REACTION,
  VOX_MUTE,
  VOX_REQUEST_HELP,
  VOX_REQUESTING_HELP,
  VOX_SEND_TONE,
  SET_VOX_STATUS,
  VOX_SET_PBX_ID,
  VOX_SET_CALL_ID,
  VOX_SUPERVISING,
  VOX_TRANSFER,
  VOX_UNBAN,
  VOX_UNBANNING,
  VOX_VOTE_NEED,
  VOX_WITHDRAW,
  VOX_DESTROY,
  VOX_INCIDENT,
  VOX_INCIDENT_STATUSES,
} from '../../consts/vox';

import { parseContactInfo } from '../serializers/call';
import {
  parseCall,
  parseOperator,
  parseDepartment,
  serializeVoxIncidentStatusForToday,
} from '../serializers/dialer';

// eslint-disable-next-line import/no-cycle
import { createChannel, vox } from './vox';

import fetchData from '../../api/fetchData';

const NON_BLOCKING_DURATION = 6 * 1000;

import { VOX_STATES } from '@constants/vox';

const getState = state => state;

let callInstance;
let watchers = [];

const OPERATORS_UPDATE_TIMEOUT = 2000;

export const getAccessURL = pbxId =>
  fetchData(
    `${process.env.API_HOST_URL}${process.env.API_ACCESS_URL}?pbx_id=${pbxId}`
  );

export function* toggleShowing(isShown) {
  yield put({ type: TOGGLE_SHOWING, isShown });
}

export function* fetchOperators() {
  const { currentManager } = yield select(state => state.general);
  const apiUrl = `${process.env.API_HOST_URL}${process.env.API_OPERATORS}`;
  const { operators: loadedOperators } = yield safeCall(fetchData, apiUrl);

  const operators = yield all(
    loadedOperators.map(operator =>
      call(parseOperator, operator, currentManager)
    )
  );
  yield put({
    operators,
    type: SET_OPERATORS,
  });

  yield delay(OPERATORS_UPDATE_TIMEOUT);
  const { dialer: state } = yield select(getState);
  if (state.operatorsListOpened) {
    yield put({ type: FETCH_OPERATORS });
  }
}

export function* fetchDepartments() {
  const apiUrl = `${process.env.API_HOST_URL}${process.env.API_DEPARTMENTS_AVAILABILITY}`;
  const { queues: loadedDepartments } = yield safeCall(fetchData, apiUrl);

  const departments = yield all(
    loadedDepartments.map(queue => call(parseDepartment, queue))
  );
  yield put({
    departments,
    type: SET_DEPARTMENTS,
  });
}

export function* fetchRecentCalls() {
  const apiUrl = `${process.env.API_HOST_URL}${process.env.API_RECENT_CALLS}`;
  const { calls: loadedCalls } = yield safeCall(fetchData, apiUrl);
  const calls = yield all(loadedCalls.map(item => call(parseCall, item)));
  yield put({
    calls,
    type: SET_RECENT_CALLS,
  });
}

export function* fetchClientInfo(phone, requireNotification) {
  const apiUrl = `${process.env.API_HOST_URL}${process.env.API_CLIENT_INFO}?phone=${phone}`;
  const { info } = yield safeCall(fetchData, apiUrl);

  let body;

  const contact = parseContactInfo(info);
  const { fullName, number } = contact;

  if (fullName) {
    body = `Вам звонит ${fullName} (${number})!`;
  } else if (number) {
    body = `Вам поступил входящий звонок с номера ${number}!`;
  } else {
    body = 'Вам поступил входящий звонок!';
  }

  yield put({ contact, type: SET_CLIENT_INFO });

  // Check that call still in state of ringing in a moment of load client info
  const state = yield select(getState);
  const isCallRinging = get(state, 'dialer.state') === VOX_STATES.INCOMING;

  if (isCallRinging && requireNotification) {
    yield put({
      payload: { title: 'Входящий звонок', body },
      type: SHOW_NOTIFICATION,
    });
  }
}

function* addLogToCall(name, data = {}) {
  const { dialer } = yield select(getState);
  const { pbxId } = dialer;

  if (!pbxId) return;

  yield safeCall(
    fetchData,
    `${process.env.API_HOST_URL}${process.env.API_ADD_LOG_TO_CALL}`,
    {
      method: 'POST',
      body: JSON.stringify({
        kind: 'crm_action',
        name,
        pbx_id: pbxId,
        show_to_manager: true,
        time_stamp: new Date(),
        data: JSON.stringify(data),
      }),
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

function* logReaction() {
  if (!callInstance) return;

  const { dialer: state } = yield select(getState);
  const { operator, callIncomingTime } = state;
  const reaction = parseInt(performance.now() - callIncomingTime, 10);
  const callerNumber = callInstance.number().replace(/\D+/g, '');

  callInstance.sendMessage(
    JSON.stringify({
      reaction,
      operatorId: operator.number,
      callee_number: operator.number,
      caller_number: callerNumber,
      type: 'log',
    })
  );

  yield put({
    type: VOX_LOG_REACTION,
  });
}

function* stopWatchers() {
  yield watchers.forEach(channel => channel.close());
}

function* watchPageUnload() {
  if (!callInstance) return;

  const handleUnloadPrompt = event => {
    event.preventDefault();
    event.returnValue = '';
  };
  const unsubscribe = () =>
    window.removeEventListener('beforeunload', handleUnloadPrompt);

  unsubscribe();
  window.addEventListener('beforeunload', handleUnloadPrompt);

  watchers = [...watchers, { close: unsubscribe }];
}

function* onConnectedCall() {
  const { dialer, general } = yield select(getState);
  const { isIncoming, isDeanonymised, isInterception } = dialer;

  const { isMasterInstance } = general;

  if (!isMasterInstance) return;

  yield put({
    operatorState: VOX_STATES.IN_SERVICE,
    type: SET_VOX_STATUS,
  });

  yield put({ type: VOX_CALL_CONNECTED });

  if (!isInterception && isIncoming) {
    yield safeCall(logReaction);
  }

  if (isInterception && !isDeanonymised) {
    yield put({
      isMuted: true,
      type: VOX_MUTE,
    });
  }
}

function* onDisconnectedCall() {
  const { dialer: dialerState, calls: callsState } = yield select(getState);
  const { state, isAnswered, isIncoming, callIncomingTime } = dialerState;

  const { selectedListeningPbxId } = callsState;
  const disconnectingTime = performance.now();
  const dialingTime = disconnectingTime - callIncomingTime;
  const forceUnban =
    isIncoming && !isAnswered && dialingTime < NON_BLOCKING_DURATION;

  if (isIncoming) {
    yield safeCall(logReaction);
  }

  if (state !== VOX_STATES.INCOMING) {
    yield put({
      operatorState: VOX_STATES.AFTER_SERVICE,
      type: SET_VOX_STATUS,
    });
  }

  yield put({
    forceUnban,
    type: VOX_CALL_DISCONNECTED,
  });

  if (selectedListeningPbxId) {
    yield put({ type: VOX_LISTEN_STOP });
  }

  yield safeCall(stopWatchers);
  yield vox.getOperatorACDStatus();
  callInstance = null;
}

function* onCheckCallQuality({ stats }) {
  console.log('CALL STATISTIC', { stats });
}

function* onMessageReceived({ text }) {
  if (!callInstance) return;

  const data = JSON.parse(text);
  const {
    type,
    callID,
    pbxID,
    isDeanonymised,
    isSupervising,
    isMuted,
    isVoteNeed,
    pushNotification,
    isRequestingHelp,
  } = data;

  if (pbxID) {
    yield put({
      pbxID,
      type: VOX_SET_PBX_ID,
    });
  }

  if (callID) {
    yield put({
      callID,
      type: VOX_SET_CALL_ID,
    });
  }

  if (typeof isDeanonymised === 'boolean') {
    if (isDeanonymised === true && pushNotification === true) {
      yield put({
        payload: {
          title: 'Входящий запрос',
          body: 'Вам поступил входящий запрос от супервизора!',
        },
        type: SHOW_NOTIFICATION,
      });
    }

    yield put({
      isDeanonymised,
      type: SET_DEANINOMIZE,
    });
  }

  if (typeof isVoteNeed === 'boolean') {
    yield put({
      isVoteNeed,
      type: VOX_VOTE_NEED,
    });
  }

  if (typeof isRequestingHelp === 'boolean') {
    yield put({
      isRequestingHelp,
      type: VOX_REQUESTING_HELP,
    });
  }

  if (typeof isSupervising === 'boolean') {
    yield put({
      isSupervising,
      type: VOX_SUPERVISING,
    });

    if (typeof isMuted === 'boolean') {
      yield put({
        isMuted,
        type: VOX_MUTE,
      });
    }
  }

  if (type === 'transfer_fail') {
    callInstance.unmuteMicrophone();
    callInstance.unmutePlayback();

    yield put({
      type: SET_TRANSFER_FAIL,
    });
  }
}

function* watchConnectedCall() {
  if (!callInstance) return;

  const { Connected } = VoxImplant.CallEvents;
  const channel = yield safeCall(createChannel, Connected, callInstance);
  watchers = [...watchers, channel];

  while (true) {
    try {
      yield take(channel);
      yield put({ type: STOP_TITLE_FLASHING });
      yield safeCall(onConnectedCall);
    } catch (err) {
      yield put({ type: STOP_TITLE_FLASHING });
    }
  }
}

function* watchDisconnectedCall() {
  if (!callInstance) return;

  const { Disconnected } = VoxImplant.CallEvents;
  const channel = yield safeCall(createChannel, Disconnected, callInstance);
  watchers = [...watchers, channel];

  try {
    yield take(channel);
    yield put({ type: STOP_TITLE_FLASHING });
    yield safeCall(onDisconnectedCall);
  } catch (err) {
    yield put({ type: STOP_TITLE_FLASHING });
  }
}

function* watchFailedCall() {
  if (!callInstance) return;

  const { Failed } = VoxImplant.CallEvents;
  const channel = yield safeCall(createChannel, Failed, callInstance);
  watchers = [...watchers, channel];

  try {
    yield take(channel);
    yield put({ type: STOP_TITLE_FLASHING });
    yield safeCall(onDisconnectedCall);
  } catch (err) {
    yield put({ type: STOP_TITLE_FLASHING });
  }
}

function* watchCallQuality() {
  if (!callInstance) return;

  const { CallStatsReceived } = VoxImplant.CallEvents;
  const channel = yield safeCall(
    createChannel,
    CallStatsReceived,
    callInstance
  );
  watchers = [...watchers, channel];

  while (true) {
    const payload = yield take(channel);
    yield call(onCheckCallQuality, payload);
  }
}

function* watchMessageReceived() {
  if (!callInstance) return;

  const { MessageReceived } = VoxImplant.CallEvents;
  const channel = yield safeCall(createChannel, MessageReceived, callInstance);
  watchers = [...watchers, channel];

  while (true) {
    try {
      const payload = yield take(channel);
      yield onMessageReceived(payload);
    } catch (err) {
      yield put({ type: STOP_TITLE_FLASHING });
    }
  }
}

export function* onIncomingCall({ call: incomingCall, headers }) {
  callInstance = incomingCall;

  const number = callInstance.number().replace(/\D+/g, '');
  const clientName = headers && headers['X-CallerName'];
  const pbxId = headers && headers['X-PBXId'];
  const isInterception = headers && headers['X-supervisor'] === 'true';
  const isDeanonymised = headers && headers['X-deanonymised'] === 'true';
  const requireNotification = !isInterception;

  yield put({
    number,
    clientName,
    isInterception,
    isDeanonymised,
    pbxId,
    type: VOX_CALL_INCOMING,
  });

  if (isInterception && !isDeanonymised) {
    yield put({ type: VOX_ANSWER });
  }

  yield all([
    safeCall(toggleShowing, true),
    safeCall(fetchClientInfo, number, requireNotification),
    safeCall(watchConnectedCall),
    safeCall(watchDisconnectedCall),
    safeCall(watchFailedCall),
    safeCall(watchMessageReceived),
    safeCall(watchCallQuality),
    safeCall(watchPageUnload),
    safeCall(checkVoxIncidents),
  ]);
}

function* createCall({ number, partner }) {
  const { dialer: dialerState, general: generalState } = yield select(getState);
  const { operator } = dialerState;
  const { isMasterInstance } = generalState;

  if (!isMasterInstance) return;

  if (number === operator.number) return;

  callInstance = yield vox.call(
    number,
    false,
    JSON.stringify({
      from: operator.number,
      to: number,
      partner,
    })
  );

  yield put({
    number,
    type: VOX_CALL_OUTGOING,
  });

  yield all([
    safeCall(fetchClientInfo, number),
    safeCall(watchConnectedCall),
    safeCall(watchDisconnectedCall),
    safeCall(watchFailedCall),
    safeCall(watchMessageReceived),
    safeCall(watchCallQuality),
    safeCall(watchPageUnload),
    safeCall(checkVoxIncidents),
  ]);
}

function* mute({ isMuted }) {
  if (!callInstance) return;

  if (isMuted) {
    yield callInstance.muteMicrophone();
  } else {
    yield callInstance.unmuteMicrophone();
  }
}

function* hold({ isHold: enable }) {
  if (!callInstance) return;

  if (enable) {
    yield callInstance.muteMicrophone();
    yield callInstance.mutePlayback();
  } else {
    yield callInstance.unmuteMicrophone();
    yield callInstance.unmutePlayback();
  }

  callInstance.sendMessage(
    JSON.stringify({
      enable,
      type: 'hold',
    })
  );
}

function* withdraw() {
  if (!callInstance) return;

  const { dialer: state } = yield select(getState);
  yield safeCall(mute, false);

  yield put({
    isMuted: false,
    type: VOX_MUTE,
  });

  yield callInstance.sendMessage(
    JSON.stringify({
      operator_id: state.operator.number,
      type: 'withdraw_call',
    })
  );

  yield safeCall(logReaction);
}

function* hangup() {
  if (!callInstance) return;

  yield callInstance.hangup();
  yield safeCall(addLogToCall, 'hangup');
}

function* answer() {
  if (!callInstance) return;

  yield callInstance.answer();
  yield safeCall(addLogToCall, 'answer');
}

function* unban() {
  const { dialer: state, general } = yield select(getState);
  const { manualState } = state;
  const { isMasterInstance } = general;

  if (!isMasterInstance) return;

  yield put({
    isUnbanning: true,
    type: VOX_UNBANNING,
  });

  yield put({
    operatorState: VOX_STATES.ONLINE,
    type: SET_VOX_STATUS,
  });

  yield delay(10);

  yield put({
    operatorState: manualState,
    type: SET_VOX_STATUS,
  });

  yield delay(500);

  yield put({
    isUnbanning: false,
    type: VOX_UNBANNING,
  });
}

function* transfer({ operatorId, queueId }) {
  if (!callInstance) return;

  yield callInstance.muteMicrophone();
  yield callInstance.mutePlayback();

  if (operatorId) {
    yield callInstance.sendMessage(
      JSON.stringify({
        operatorId,
        type: 'transfer',
      })
    );

    yield safeCall(addLogToCall, 'transfer', { operatorId });
  } else {
    yield callInstance.sendMessage(
      JSON.stringify({
        queueId,
        type: 'transfer',
      })
    );

    yield safeCall(addLogToCall, 'transfer', { queueId });
  }
}

function* deaninomize({ isDeanonymised: enable }) {
  if (!callInstance) return;

  yield callInstance.sendMessage(
    JSON.stringify({
      enable,
      type: 'deanonymise',
    })
  );
}

function* sendTone({ key }) {
  if (!callInstance) return;

  yield callInstance.sendMessage(
    JSON.stringify({
      key,
      type: 'tone',
    })
  );
}

function* connectSupervisor({ isConnected: connected }) {
  if (!callInstance) return;

  yield callInstance.sendMessage(
    JSON.stringify({
      connected,
      type: 'connect_superviser',
    })
  );
}

export function* listen({ pbxId }) {
  const { dialer: state } = yield select(getState);
  const supervisorID = state.operator.number;
  const { access_url: accessURL } = yield safeCall(getAccessURL, pbxId);

  if (!accessURL) {
    yield put({ error: 'Пустой access_url', type: LISTEN_CALL_FAILURE });
    return;
  }

  yield safeCall(
    fetchData,
    accessURL,
    {
      method: 'POST',
      body: JSON.stringify({
        type: 'listen',
        supervisorID,
      }),
      credentials: 'omit',
    },
    false
  );

  yield put({ payload: { pbxId }, type: VOX_LISTEN_START });
  yield put({ payload: { pbxId }, type: SELECT_CALL_BY_PBX_ID });
}

export function* requestHelp({ supervisorID }) {
  const { dialer } = yield select(getState);
  const { pbxId } = dialer;
  const { access_url: accessURL } = yield safeCall(getAccessURL, pbxId);

  yield safeCall(
    fetchData,
    accessURL,
    {
      method: 'POST',
      body: JSON.stringify({
        type: 'help_request',
        supervisorID,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'omit',
    },
    false
  );
}

function* cancelHelpRequest() {
  if (!callInstance) return;

  yield callInstance.sendMessage(
    JSON.stringify({
      type: 'cancel_help_request',
    })
  );
}

function* triggerSyncVoxStatus({ isOpen }) {
  if (!isOpen) {
    return;
  }

  const { dialer } = yield select(getState);
  const { state, isConnected } = dialer;
  yield isConnected &&
    state !== VOX_STATES.INCOMING &&
    vox.getOperatorACDStatus();

  yield safeCall(checkVoxIncidents);
}

function* toggleOperators({ isOpen }) {
  if (!isOpen) {
    return;
  }
  yield put({ type: FETCH_OPERATORS });
}

function* toggleDepartments({ isOpen }) {
  if (!isOpen) {
    return;
  }
  yield put({ type: FETCH_DEPARTMENTS });
}

function* toggleRecentCalls({ isOpen }) {
  if (!isOpen) {
    return;
  }
  yield put({ type: FETCH_RECENT_CALLS });
}

async function getVoxIssues() {
  const res = await parseRSS(process.env.VOX_STATUS_URL);
  return res?.items;
}

function* checkVoxIncidents() {
  const issues = yield safeCall(getVoxIssues);

  if (!issues) return;

  const status = serializeVoxIncidentStatusForToday(issues);
  if (status !== VOX_INCIDENT_STATUSES.INVESTIGATING) return;

  yield put({ type: VOX_INCIDENT });
}

export default function* () {
  yield all([
    yield takeEvery(VOX_CALL, safeApply(createCall)),
    yield takeEvery(VOX_ANSWER, safeApply(answer)),
    yield takeEvery(VOX_HANGUP, safeApply(hangup)),
    yield takeEvery(VOX_UNBAN, safeApply(unban)),
    yield takeEvery(VOX_HOLD, safeApply(hold)),
    yield takeEvery(VOX_MUTE, safeApply(mute)),
    yield takeEvery(VOX_LISTEN, safeApply(listen)),
    yield takeEvery(VOX_CONNECT_SUPERVISOR, safeApply(connectSupervisor)),
    yield takeEvery(VOX_REQUEST_HELP, safeApply(requestHelp)),
    yield takeEvery(VOX_CANCEL_HELP_REQUEST, safeApply(cancelHelpRequest)),
    yield takeEvery(VOX_SEND_TONE, safeApply(sendTone)),
    yield takeEvery(VOX_DEANINOMIZE, safeApply(deaninomize)),
    yield takeEvery(VOX_TRANSFER, safeApply(transfer)),
    yield takeEvery(VOX_WITHDRAW, safeApply(withdraw)),
    yield takeEvery(TOGGLE_OPERATORS, safeApply(toggleOperators)),
    yield takeEvery(FETCH_OPERATORS, safeApply(fetchOperators)),
    yield takeEvery(FETCH_DEPARTMENTS, safeApply(fetchDepartments)),
    yield takeEvery(FETCH_RECENT_CALLS, safeApply(fetchRecentCalls)),
    yield takeEvery(TOGGLE_DEPARTMENTS, safeApply(toggleDepartments)),
    yield takeEvery(TOGGLE_RECENT_CALLS, safeApply(toggleRecentCalls)),
    yield takeEvery(VOX_DESTROY, safeApply(stopWatchers)),
    yield takeEvery(TOGGLE_BUGS_DROPDOWN, safeApply(triggerSyncVoxStatus)),
  ]);
}
